import { motion } from "framer-motion";
import Image from "next/image";
import { useEffect, useState } from "react";

// Clean color palette - Only Deep Teal Green and White
const colors = {
  primary: "#395750",      // Deep Teal Green - Company color
  primaryDark: "#2A423C",  // Darker teal
  primaryLight: "#4B6B63", // Lighter teal
  white: "#FFFFFF",        // White
  whiteOff: "#F8F9FA",     // Off-white for subtle contrast
  textDark: "#1A2E29",     // Dark text for contrast
};

const SocialContributions = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [socialContributions, setSocialContributions] = useState([]);

  // Fetch data from API
  useEffect(() => {
    const fetchSocialContributions = async () => {
      try {
        const response = await fetch('https://ymfierp.yegnamicrofinance.com/api/social-contribution');
        const data = await response.json();
        setSocialContributions(data);
      } catch (error) {
        console.error('Error fetching social contributions:', error);
      }
    };

    fetchSocialContributions();
  }, []);

  // Auto-rotate carousel
  useEffect(() => {
    if (socialContributions.length <= 1) return () => {};
    const interval = setInterval(() => {
      setActiveIndex((prev) =>
        prev === socialContributions.length - 1 ? 0 : prev + 1,
      );
    }, 5000);
    return () => clearInterval(interval);
  }, [socialContributions.length]);

  const nextSlide = () => {
    setActiveIndex((prev) =>
      prev === socialContributions.length - 1 ? 0 : prev + 1,
    );
  };

  const prevSlide = () => {
    setActiveIndex((prev) =>
      prev === 0 ? socialContributions.length - 1 : prev - 1,
    );
  };

  return (
    <section className="relative overflow-hidden py-12 md:py-16 lg:py-24" style={{ backgroundColor: colors.white }}>
      {/* Decorative Elements */}
      <div 
        className="absolute right-0 top-0 -z-10 h-80 w-80 rounded-full blur-[100px]"
        style={{ backgroundColor: `${colors.primary}10` }}
      />
      <div 
        className="absolute bottom-0 left-0 -z-10 h-80 w-80 rounded-full blur-[100px]"
        style={{ backgroundColor: `${colors.primary}10` }}
      />

      {/* Additional decorative elements */}
      <div 
        className="absolute -left-20 top-1/3 h-40 w-40 rounded-full blur-[80px]"
        style={{ backgroundColor: `${colors.primary}05` }}
      />
      <div 
        className="absolute -right-20 bottom-1/3 h-40 w-40 rounded-full blur-[80px]"
        style={{ backgroundColor: `${colors.primary}05` }}
      />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-12 text-center md:mb-16"
        >
          <h2 className="text-3xl font-bold leading-tight md:text-4xl lg:text-5xl" style={{ color: colors.textDark }}>
            <span 
              className="bg-gradient-to-r bg-clip-text text-transparent"
              style={{
                backgroundImage: `linear-gradient(to right, ${colors.primaryDark}, ${colors.primary}, ${colors.primaryLight})`
              }}
            >
              Social Responsibility & Community Impact
            </span>
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-lg md:mt-6" style={{ color: colors.primary }}>
            Committed to creating lasting positive change through sustainable
            financial initiatives
          </p>
        </motion.div>

        <div className="flex flex-col items-center gap-8 lg:flex-row lg:gap-12">
          {/* Carousel Section - Left Side */}
          <div className="w-full lg:w-[58%]">
            <div className="relative overflow-hidden rounded-3xl shadow-2xl">
              {/* Carousel Slides */}
              <div
                className="flex transition-transform duration-700 ease-out"
                style={{ transform: `translateX(-${activeIndex * 100}%)` }}
              >
                {socialContributions.map((contribution, index) => (
                  <div
                    key={contribution.id}
                    className="relative h-[500px] w-full flex-shrink-0 md:h-[600px]"
                  >
                    <Image
                      src={`https://ymfierp.yegnamicrofinance.com//${contribution.image_path}`}
                      alt={contribution.title}
                      fill
                      className="object-cover"
                      priority={index === activeIndex}
                      sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 60vw"
                      unoptimized
                    />
                    {/* Enhanced Overlay Content */}
                    <div 
                      className="absolute inset-0 p-6 sm:p-8"
                      style={{
                        background: `linear-gradient(to top, ${colors.primary}E6, ${colors.primary}40, transparent)`
                      }}
                    >
                      <motion.div
                        className="mx-auto flex h-full max-w-2xl flex-col items-start justify-end"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2, duration: 0.4 }}
                      >
                        <h3 className="text-2xl font-bold text-white sm:text-3xl md:text-4xl">
                          {contribution.title}
                        </h3>
                        <p className="mt-3 text-white/90 sm:text-lg md:mt-4">
                          {contribution.description}
                        </p>
                        {/* <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          className="mt-5 rounded-xl px-6 py-2.5 text-base text-white backdrop-blur-lg transition-all sm:px-8 sm:py-3 sm:text-lg"
                          style={{ backgroundColor: `${colors.white}33` }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = `${colors.white}50`;
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = `${colors.white}33`;
                          }}
                        >
                          {contribution.button_text} →
                        </motion.button> */}
                      </motion.div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Carousel Controls */}
              {socialContributions.length > 1 && (
                <>
                  <motion.button
                    onClick={prevSlide}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    className="absolute left-4 top-1/2 -translate-y-1/2 rounded-full p-2 shadow-lg backdrop-blur-lg transition-all sm:left-6 sm:p-3"
                    style={{ backgroundColor: `${colors.white}80` }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = `${colors.white}CC`;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = `${colors.white}80`;
                    }}
                  >
                    <span className="text-xl sm:text-2xl" style={{ color: colors.primary }}>←</span>
                  </motion.button>

                  <motion.button
                    onClick={nextSlide}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    className="absolute right-4 top-1/2 -translate-y-1/2 rounded-full p-2 shadow-lg backdrop-blur-lg transition-all sm:right-6 sm:p-3"
                    style={{ backgroundColor: `${colors.white}80` }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = `${colors.white}CC`;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = `${colors.white}80`;
                    }}
                  >
                    <span className="text-xl sm:text-2xl" style={{ color: colors.primary }}>→</span>
                  </motion.button>
                </>
              )}

              {/* Carousel Indicators */}
              {socialContributions.length > 1 && (
                <div className="absolute bottom-4 left-1/2 flex -translate-x-1/2 space-x-2 sm:bottom-6">
                  {socialContributions.map((_, index) => (
                    <motion.button
                      key={index}
                      onClick={() => setActiveIndex(index)}
                      whileHover={{ scale: 1.2 }}
                      whileTap={{ scale: 0.8 }}
                      className={`h-2 rounded-full transition-all ${
                        activeIndex === index ? "w-6 shadow-md" : "w-2"
                      }`}
                      style={{
                        backgroundColor: activeIndex === index
                          ? colors.white
                          : `${colors.white}80`
                      }}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Right Side Content */}
          <div className="w-full lg:w-[42%]">
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              {/* Initiative List */}
              <motion.div
                className="space-y-4 sm:space-y-5"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.4 }}
              >
                {[
                  {
                    title: "Financial Literacy Programs",
                    description:
                      "Educating communities about financial management strategies",
                    icon: (
                      <svg
                        className="h-5 w-5 transition-colors duration-300 sm:h-6 sm:w-6"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                        />
                      </svg>
                    ),
                  },
                  {
                    title: "Sustainable Investments",
                    description:
                      "Funding renewable energy projects and green infrastructure",
                    icon: (
                      <svg
                        className="h-5 w-5 transition-colors duration-300 sm:h-6 sm:w-6"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
                        />
                      </svg>
                    ),
                  },
                  {
                    title: "Community Development",
                    description:
                      "Supporting small businesses and local entrepreneurship",
                    icon: (
                      <svg
                        className="h-5 w-5 transition-colors duration-300 sm:h-6 sm:w-6"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                        />
                      </svg>
                    ),
                  },
                ].map((initiative, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: 0.5 + index * 0.1 }}
                    className="group relative flex items-start space-x-3 overflow-hidden rounded-xl border p-3 shadow-sm transition-all hover:shadow-md sm:space-x-4 sm:p-4"
                    style={{ 
                      borderColor: `${colors.primary}15`,
                      backgroundColor: colors.white
                    }}
                    whileHover={{ y: -2 }}
                  >
                    <div 
                      className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg p-2 transition-all sm:h-12 sm:w-12"
                      style={{ backgroundColor: `${colors.primary}10` }}
                    >
                      <div style={{ color: colors.primary }}>
                        {initiative.icon}
                      </div>
                    </div>
                    <div>
                      <h3 className="text-base font-semibold sm:text-lg" style={{ color: colors.textDark }}>
                        {initiative.title}
                      </h3>
                      <p className="mt-1 text-sm sm:text-base" style={{ color: colors.textDark }}>
                        {initiative.description}
                      </p>
                    </div>
                    <div 
                      className="absolute -bottom-3 -right-3 h-12 w-12 rounded-full transition-all"
                      style={{ backgroundColor: `${colors.primary}05` }}
                    />
                  </motion.div>
                ))}
              </motion.div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SocialContributions;